import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ClipboardList, Users, Calendar, Clock, MapPin, XCircle, CalendarOff } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import SessionPlanner from "./SessionPlanner";
import SessionAttendance from "./SessionAttendance";

interface Props {
  initialSeasonId?: string | null;
}

type ViewMode = "list" | "planner" | "attendance";

export default function SessionList({ initialSeasonId }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [seasonId, setSeasonId] = useState<string>(initialSeasonId || "");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkFrom, setBulkFrom] = useState("");
  const [bulkTo, setBulkTo] = useState("");
  const [bulkReason, setBulkReason] = useState("");

  const { data: seasons = [] } = useQuery({
    queryKey: ["admin-seasons", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("seasons")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["admin-groups-for-sessions", seasonId, organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_groups")
        .select("id, name, color")
        .eq("season_id", seasonId)
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!seasonId && !!organization?.id,
  });

  const seasonGroupIds = groups.map((g) => g.id);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["admin-sessions", seasonId, groupFilter, organization?.id],
    queryFn: async () => {
      let q = supabase
        .from("training_sessions")
        .select("*, training_groups(name, color), profiles!training_sessions_coach_id_fkey(full_name), courts(name)")
        .eq("organization_id", organization!.id)
        .order("session_date", { ascending: true })
        .order("start_time", { ascending: true });

      if (groupFilter && groupFilter !== "all") {
        q = q.eq("group_id", groupFilter);
      } else if (seasonGroupIds.length > 0) {
        q = q.in("group_id", seasonGroupIds);
      } else {
        return [];
      }
      const { data } = await q;
      return data ?? [];
    },
    enabled: !!organization?.id && !!seasonId && seasonGroupIds.length > 0,
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { error } = await supabase
        .from("training_sessions")
        .update({ status: "cancelled", cancelled_reason: reason })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
      setCancellingId(null);
      setCancelReason("");
      toast({ title: t("training.sessionCancelled") });
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("training_sessions")
        .update({ status: "completed" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
      toast({ title: t("training.sessionCompleted") });
    },
  });

  const bulkCancelMutation = useMutation({
    mutationFn: async () => {
      let q = supabase
        .from("training_sessions")
        .update({ status: "cancelled" as const, cancelled_reason: bulkReason })
        .eq("organization_id", organization!.id)
        .eq("status", "scheduled")
        .gte("session_date", bulkFrom)
        .lte("session_date", bulkTo);
      if (groupFilter && groupFilter !== "all") {
        q = q.eq("group_id", groupFilter);
      } else if (seasonGroupIds.length > 0) {
        q = q.in("group_id", seasonGroupIds);
      }
      const { error } = await q;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
      setBulkDialog(false);
      setBulkFrom("");
      setBulkTo("");
      setBulkReason("");
      toast({ title: t("training.sessionsBulkCancelled") });
    },
  });

  // Auto-select first season
  if (!seasonId && seasons.length > 0) {
    setSeasonId(seasons[0].id);
  }

  if (viewMode === "planner" && selectedSessionId) {
    return (
      <SessionPlanner
        sessionId={selectedSessionId}
        onBack={() => {
          setViewMode("list");
          setSelectedSessionId(null);
        }}
      />
    );
  }

  if (viewMode === "attendance" && selectedSessionId) {
    return (
      <SessionAttendance
        sessionId={selectedSessionId}
        onBack={() => {
          setViewMode("list");
          setSelectedSessionId(null);
        }}
      />
    );
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case "scheduled":
        return <Badge variant="outline">{t("training.statusScheduled")}</Badge>;
      case "completed":
        return <Badge className="bg-primary text-primary-foreground">{t("training.statusCompleted")}</Badge>;
      case "cancelled":
        return <Badge variant="destructive">{t("training.statusCancelled")}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Select value={seasonId} onValueChange={setSeasonId}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("training.selectSeason")} />
          </SelectTrigger>
          <SelectContent>
            {seasons.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("training.allGroups")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("training.allGroups")}</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBulkDialog(true)}>
          <CalendarOff className="h-3.5 w-3.5" /> {t("training.cancelPeriod")}
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground text-sm">{t("training.loadingSessions")}</p>}

      {!isLoading && sessions.length === 0 && (
        <p className="text-muted-foreground text-sm">{t("training.noSessionsFound")}</p>
      )}

      <div className="space-y-2">
        {sessions.map((s: any) => (
          <Card key={s.id}>
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  {s.training_groups?.color && (
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.training_groups.color }} />
                  )}
                  <div className="flex items-center gap-1.5 text-sm">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    {format(new Date(s.session_date), "EEE d MMM", { locale: sv })}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {s.start_time?.slice(0, 5)} ({s.duration_minutes} min)
                  </div>
                  <span className="text-sm font-medium">{s.training_groups?.name}</span>
                  {s.profiles?.full_name && (
                    <span className="text-sm text-muted-foreground">{s.profiles.full_name}</span>
                  )}
                  {s.courts?.name && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /> {s.courts.name}
                    </div>
                  )}
                  {statusBadge(s.status)}
                </div>
                <div className="flex items-center gap-1.5">
                  {s.status === "scheduled" && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSessionId(s.id);
                          setViewMode("planner");
                        }}
                      >
                        <ClipboardList className="h-3.5 w-3.5 mr-1" /> {t("training.plan")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSessionId(s.id);
                          setViewMode("attendance");
                        }}
                      >
                        <Users className="h-3.5 w-3.5 mr-1" /> {t("training.attendance")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => completeMutation.mutate(s.id)}>
                        {t("training.completed")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCancellingId(cancellingId === s.id ? null : s.id)}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {cancellingId === s.id && (
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder={t("training.reasonPlaceholder")}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => cancelMutation.mutate({ id: s.id, reason: cancelReason })}
                  >
                    {t("training.cancel")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bulk cancel dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("training.cancelSessionsForPeriod")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("training.fromDate")}</Label>
              <Input type="date" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)} />
            </div>
            <div>
              <Label>{t("training.toDate")}</Label>
              <Input type="date" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} />
            </div>
            <div>
              <Label>{t("training.reason")}</Label>
              <Input placeholder={t("training.reasonExamplePlaceholder")} value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground">
              {groupFilter && groupFilter !== "all" ? t("training.bulkCancelSelectedGroup") : t("training.bulkCancelAllGroups")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(false)}>{t("training.abort")}</Button>
            <Button
              variant="destructive"
              disabled={!bulkFrom || !bulkTo || bulkCancelMutation.isPending}
              onClick={() => bulkCancelMutation.mutate()}
            >
              {t("training.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
